import { type ReactNode, useEffect, useRef } from 'react';

/**
 * The demo's dialog: backdrop, elevated card, `modalIn` entrance, Escape to close, focus
 * moved in on open and restored on close. Every colour is a token; the elevation is
 * `--elev-modal`, which is composed from `--text` so it follows palette x mode.
 */
export function Modal({
  title,
  lead,
  width = 'min(540px,100%)',
  dismissable = true,
  onClose,
  footer,
  children,
}: {
  title: string;
  lead?: string;
  /**
   * The demo sizes its dialogs from their content: the input and confirmation dialogs are
   * 540px, the audit-entry detail is 760px because its context table and raw JSON need the
   * room. One prop rather than a second dialog component — everything else is identical.
   */
  width?: string;
  /**
   * Defaults true so every existing caller is unaffected. The one exception is a dialog that
   * gates its own close behind an explicit acknowledgement (the shown-once application
   * secret) — for that caller, `false` disables Escape and the backdrop click, so neither
   * can bypass the gate. The footer's own close control is that caller's to disable, not
   * this component's: `onClose` still reaches it, only the two AMBIENT dismissal paths stop.
   */
  dismissable?: boolean;
  onClose: () => void;
  footer: ReactNode;
  children?: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const previous = document.activeElement;
    dialogRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && dismissable) onClose();
    }
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [onClose, dismissable]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 28,
      }}
    >
      <div
        className="backdrop"
        style={{ position: 'absolute', inset: 0 }}
        onClick={dismissable ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          position: 'relative',
          width,
          maxHeight: '88vh',
          overflow: 'auto',
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 14,
          boxShadow: 'var(--elev-modal)',
          animation: 'modalIn .16s ease-out',
          outline: 'none',
        }}
      >
        <div style={{ padding: '20px 22px 0' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 650, letterSpacing: '-.01em' }}>
            {title}
          </h2>
          {lead === undefined ? null : (
            <p className="tx-subtle" style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.55 }}>
              {lead}
            </p>
          )}
        </div>
        {children}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 9,
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            background: 'var(--surface-2)',
            borderRadius: '0 0 13px 13px',
          }}
        >
          {footer}
        </div>
      </div>
    </div>
  );
}

/** Form body: the stacked-field padding the demo's input dialogs use. */
export function ModalForm({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {children}
    </div>
  );
}

/** Prose body: the tighter padding the demo's confirmation dialogs use. */
export function ModalProse({ children }: { children: ReactNode }) {
  return <div style={{ padding: '14px 22px 18px' }}>{children}</div>;
}

export function ModalParagraph({ children }: { children: ReactNode }) {
  return (
    <p
      className="tx-muted"
      style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, textWrap: 'pretty' }}
    >
      {children}
    </p>
  );
}
